/**
 * One inbox connection per organization, shared by the gutter badge and the
 * Requests surface.
 *
 * It is mounted at the organization shell rather than inside the Requests route
 * on purpose: the badge has to be right while the reader is in Shared Docs, and
 * the presence heartbeat has to be running while they are anywhere in the
 * console — a reviewer who looks offline to their teammates is the failure this
 * connection exists to prevent.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { TeamInboxSnapshot } from '@nimbalyst/collab-bundle/inbox';

import type { TeamJwt, TeamMemberId } from '../auth/teamJwt';
import { BrowserTeamInboxClient } from './BrowserTeamInboxClient';
import { countUnreadInboxRows } from './requestsInboxModel';

const EMPTY_SNAPSHOT: TeamInboxSnapshot = {
  status: 'loading',
  deliveries: [],
  organizations: [],
};

export interface RequestsInboxValue {
  snapshot: TeamInboxSnapshot;
  /** Unread rows admitted by the Requests surface. */
  unreadCount: number;
  markRead: (deliveryIds: string[]) => void;
  markAllRead: () => void;
  dismiss: (deliveryId: string) => void;
}

/**
 * The default is a live-shaped value, not `null`. The gutter renders above the
 * provider on the session-failure and still-connecting paths, and a badge that
 * throws there would take the whole shell with it.
 */
const RequestsInboxContext = createContext<RequestsInboxValue>({
  snapshot: EMPTY_SNAPSHOT,
  unreadCount: 0,
  markRead: () => undefined,
  markAllRead: () => undefined,
  dismiss: () => undefined,
});

export function useRequestsInbox(): RequestsInboxValue {
  return useContext(RequestsInboxContext);
}

export function RequestsInboxProvider({
  orgId,
  orgName,
  teamMemberId,
  getTeamJwt,
  children,
}: {
  orgId: string;
  orgName: string;
  /**
   * Null until the org's team session resolves, and after it fails. The team
   * inbox room is addressed by the org-scoped member id, so there is nothing to
   * connect to without one — and this provider wraps the shell on those paths
   * too, so that the tree keeps its shape and the shell is not torn down and
   * rebuilt underneath the reader when the session arrives.
   */
  teamMemberId: TeamMemberId | null;
  getTeamJwt: () => Promise<TeamJwt>;
  children: ReactNode;
}) {
  const [client, setClient] = useState<BrowserTeamInboxClient | null>(null);
  const [snapshot, setSnapshot] = useState<TeamInboxSnapshot>(EMPTY_SNAPSHOT);

  // Built inside the effect rather than memoized beside it: the client is
  // destroyed on teardown and cannot be restarted, so a StrictMode double-mount
  // would otherwise hand the second mount a corpse.
  useEffect(() => {
    if (!teamMemberId) return;
    const next = new BrowserTeamInboxClient({
      org: { orgId, orgName, teamMemberId },
      getTeamJwt,
    });
    setClient(next);
    setSnapshot(next.getSnapshot());
    const unsubscribe = next.subscribe(() => setSnapshot(next.getSnapshot()));
    // Connection failures are reported through the snapshot's per-org status;
    // a rejection here only means the client was torn down mid-connect.
    void next.start().catch((error) => {
      console.error('[RequestsInbox] Team inbox connection ended', error);
    });
    return () => {
      unsubscribe();
      next.destroy();
      setClient(null);
      setSnapshot(EMPTY_SNAPSHOT);
    };
  }, [getTeamJwt, orgId, orgName, teamMemberId]);

  // Kept off `snapshot` so their identity survives every delivery that arrives.
  // A `markRead` that changed on each snapshot would re-fire the caller's
  // read-on-select effect while its own response was still in flight.
  const actions = useMemo(() => ({
    markRead: (deliveryIds: string[]) => {
      void client?.markRead(deliveryIds).catch(() => undefined);
    },
    markAllRead: () => {
      void client?.markAllVisibleRead().catch(() => undefined);
    },
    dismiss: (deliveryId: string) => {
      void client?.dismiss(deliveryId).catch(() => undefined);
    },
  }), [client]);

  const value = useMemo<RequestsInboxValue>(() => ({
    ...actions,
    snapshot,
    unreadCount: countUnreadInboxRows(snapshot.deliveries),
  }), [actions, snapshot]);

  return (
    <RequestsInboxContext.Provider value={value}>
      {children}
    </RequestsInboxContext.Provider>
  );
}
